"use client"

import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { BlockchainFiltersView } from "@/app/blockchains/filters/blockchain-filters"
import { BlockchainFiltersMoleculeReatom } from "@/app/blockchains/filters/blockchain-filters-molecule.reatom"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import * as amplitude from "@amplitude/analytics-browser"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { usePathname } from "next/navigation"
import { useCallback, useMemo } from "react"
import { filterProfileAvailableBlockchains } from "./profile-available-blockchains"
import { ProfileMoleculeReatom } from "./store/index.reatom"

function constructNewPath(currentPath: string, newBlockchain: string): string {
  const paths = currentPath.split("/").filter(Boolean)

  if (paths.length === 0) {
    return `/${newBlockchain.toLowerCase()}`
  }

  paths[0] = newBlockchain.toLowerCase()
  return `/${paths.join("/")}`
}

export const ProfileBlockchainFilters = reatomComponent<{
  className?: string
}>(({ className }) => {
  const { selectedBlockchain, availableBlockchains, setSelectedBlockchain } =
    useMolecule(BlockchainFiltersMoleculeReatom)
  const { walletAddress } = useMolecule(ProfileMoleculeReatom)
  const { isEvmBlockchain: getIsEvmBlockchain } = useMolecule(
    BlockchainsMoleculeReatom,
  )
  const selectedBlockchainValue = selectedBlockchain()
  const allBlockchains = availableBlockchains()
  const logger = useMolecule(LoggerMolecule)
  const pathname = usePathname()

  const blockchainFilters = useMemo(() => {
    const filtered = filterProfileAvailableBlockchains(
      allBlockchains,
      walletAddress,
      getIsEvmBlockchain(),
    )

    return filtered
  }, [allBlockchains, walletAddress, getIsEvmBlockchain])

  const onSelect = useCallback(
    (value: string) => {
      if (value && value !== selectedBlockchainValue) {
        const newBlockchain = value as Blockchain

        amplitude.track("Blockchain selected", {
          blockchain: value,
        })

        setSelectedBlockchain(newBlockchain)
        logger?.api.setContext({ blockchain: newBlockchain })

        const newPath = constructNewPath(pathname, newBlockchain)
        window.history.pushState(null, "", newPath)
        window.dispatchEvent(new PopStateEvent("popstate"))
      }
    },
    [selectedBlockchainValue, pathname, setSelectedBlockchain, logger],
  )

  return (
    <BlockchainFiltersView
      variant="selector"
      className={className}
      onSelect={onSelect}
      blockchains={blockchainFilters}
      selectedBlockchain={selectedBlockchainValue}
    />
  )
}, "ProfileBlockchainFilters")
